<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { HeadersData } from '@/composables/useTable';

import { computed, ref, watch } from 'vue';
import { formatInTimeZone } from 'date-fns-tz';

import KBtn from '@/components/btns/KBtn.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import useTable from '@/composables/useTable';
import { fetchPostJobMessageVisibility } from '@/helpers/fetchers';

import tableStyles from '@/styles/table.module.scss';

export interface DataItem {
  id: number;
  sender: string;
  message: string;
  timeAndDate: string;
  isPrivate: boolean;
}

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<DataItem[]>;
  isStaff?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
  isStaff: false,
});

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<DataItem[]>>
>;

const headersData = computed<HeadersData>(function () {
  if (props.isStaff) {
    return [
      {
        value: 'sender',
        title: 'Sender',
        isNotSortable: true,
      },
      {
        value: 'message',
        title: 'Message',
        isNotSortable: true,
      },
      {
        value: 'timeAndDate',
        title: 'Time and date',
        isNotSortable: true,
      },
      {
        value: 'actions',
        title: 'Actions',
        isNotSortable: true,
      },
    ];
  } else {
    return [
      {
        value: 'sender',
        title: 'Sender',
        isNotSortable: true,
      },
      {
        value: 'message',
        title: 'Message',
        isNotSortable: true,
      },
      {
        value: 'timeAndDate',
        title: 'Time and date',
        isNotSortable: true,
      },
    ];
  }
});

const foldColspan = computed(() => {
  return headersData.value.length;
});

const timeZone = 'Australia/Sydney';

function formatDate(date: string, timeZone: string): string {
  return formatInTimeZone(date, timeZone, 'hh:mm a, MMM d'); // 04:50 PM, Sep 24
}

const loadings = ref<Record<string, boolean>>({});
const updates = ref<Record<string, boolean>>({});

async function handleTogglePrivacyClick(dataItem: DataItem): Promise<void> {
  if (loadings.value[dataItem.id]) return;
  loadings.value[dataItem.id] = true;
  const isPrivate =
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    updates.value[dataItem.id] != null
      ? updates.value[dataItem.id]
      : dataItem.isPrivate;
  await fetchPostJobMessageVisibility({
    message_id: dataItem.id,
    private: !isPrivate,
  })
    .then(() => {
      updates.value[dataItem.id] = !isPrivate;
    })
    .finally(() => {
      loadings.value[dataItem.id] = false;
    });
}

watch(
  () => props.data,
  (newValue, oldValue) => {
    updates.value = {};
  },
);
</script>

<template>
  <table
    :class="[tableStyles.table, $style.detailedJobQueriesTable]"
    data-testid="detailedJobQueriesTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText>No queries</TableEmptyText>
      </td>
    </tr>
    <tbody v-else>
      <template v-if="props.isStaff">
        <template v-for="dataItem in sortedData" :key="dataItem.id">
          <tr>
            <td :class="$style.senderTd">
              {{ dataItem.sender }}
            </td>
            <td :class="$style.messageTd">
              {{ dataItem.message }}
            </td>
            <td :class="$style.timeAndDateTd">
              <span>
                <KParentTooltip>{{ dataItem.timeAndDate }}</KParentTooltip>
                {{ formatDate(dataItem.timeAndDate, timeZone) }}
              </span>
            </td>
            <td :class="$style.actionsTd">
              <menu :class="$style.actionsMenuList">
                <li :class="$style.actionsMenuListItem">
                  <KParentTooltip location="top">
                    {{
                      updates[dataItem.id] != null
                        ? updates[dataItem.id]
                          ? 'Set as public'
                          : 'Set as private'
                        : dataItem.isPrivate
                          ? 'Set as public'
                          : 'Set as private'
                    }}
                  </KParentTooltip>
                  <KBtn
                    variant="text"
                    :icon="
                      updates[dataItem.id] != null
                        ? updates[dataItem.id]
                          ? 'mdi-lock-outline'
                          : 'mdi-lock-open-variant-outline'
                        : dataItem.isPrivate
                          ? 'mdi-lock-outline'
                          : 'mdi-lock-open-variant-outline'
                    "
                    :loading="loadings[dataItem.id]"
                    @click="handleTogglePrivacyClick(dataItem)"
                  ></KBtn>
                </li>
              </menu>
            </td>
          </tr>
        </template>
      </template>
      <template v-else>
        <template v-for="dataItem in sortedData" :key="dataItem.id">
          <tr>
            <td :class="$style.senderTd">
              {{ dataItem.sender }}
            </td>
            <td :class="$style.messageTd">
              {{ dataItem.message }}
            </td>
            <td :class="$style.timeAndDateTd">
              <span>
                <KParentTooltip>{{ dataItem.timeAndDate }}</KParentTooltip>
                {{ formatDate(dataItem.timeAndDate, timeZone) }}
              </span>
            </td>
          </tr>
        </template>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.detailedJobQueriesTable {
}
  .senderTh {
  }
  .messageTh {
  }
  .timeAndDateTh {
  }
  .actionsTh {
  }
  .senderTd {
  }
  .messageTd {
  }
  .timeAndDateTd {
    white-space: nowrap;
  }
  .actionsTd {
    white-space: nowrap;
    text-align: center;
  }
    .actionsMenuList {
      list-style: none;
      padding: 0;
      margin: -12px -10px;
    }
      .actionsMenuListItem {
        display: inline;
      }
</style>
